/**
 * Deterministic JSON serialisation for use as a hash key (ENG-4468).
 *
 * The manager caches channel-config hashes to decide whether to rewrite
 * `.mcp.json` each poll. The previous implementation hashed
 * `JSON.stringify(entry.config)` directly, but Postgres JSONB doesn't
 * preserve key order — each `SELECT config FROM ...` can return the same
 * semantic object with different key ordering. `JSON.stringify` then
 * produces a different string, the hash mismatches, and the cache is
 * missed every poll. For agents with nested config objects (e.g. Slack's
 * `manifest`) the churn was constant: credentials rewritten every
 * ~1–2 min → drift detection fires → MCP subprocess gets re-read and
 * sometimes fails to respawn cleanly.
 *
 * `canonicalJson` walks the value and emits keys in sorted order at every
 * object level so two semantically-identical configs produce identical
 * output regardless of the input key order.
 */

export function canonicalJson(value: unknown): string {
  return JSON.stringify(normalize(value));
}

/**
 * Recursively rebuild the value with every object's keys in alphabetic
 * order. Arrays preserve their order (semantic). Primitives pass
 * through. Circular references would throw here the same way they
 * would in JSON.stringify, which is fine — this codepath is fed by
 * plain DB JSONB so cycles are impossible.
 */
function normalize(value: unknown): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(normalize);
  const sorted: Record<string, unknown> = {};
  const keys = Object.keys(value as Record<string, unknown>).sort();
  for (const k of keys) {
    sorted[k] = normalize((value as Record<string, unknown>)[k]);
  }
  return sorted;
}
